import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'utils/axios';
import AxiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';

const formatQueryParam = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(',') : '';
  return String(value);
};

export const buildActivityBookingsQuery = (data, options = {}) => {
  const { csv = false } = options;
  const params = new URLSearchParams();

  params.set('page', String(data.page || 1));
  params.set('limit', String(data.limit || 10));
  if (data.pageSize !== undefined && data.pageSize !== null) {
    params.set('page_size', String(data.pageSize));
  }
  if (csv) params.set('csv', 'true');
  if (data.id) params.set('id', data.id);

  params.set('booking_start_date', data.booking_start_date ?? data.startDate ?? '');
  params.set('booking_end_date', data.booking_end_date ?? data.endDate ?? '');
  params.set('travel_start_date', data.travel_start_date ?? data.travelStartDate ?? '');
  params.set('travel_end_date', data.travel_end_date ?? data.travelEndDate ?? '');
  params.set('booking_status', formatQueryParam(data.booking_status ?? data.bookingStatus));
  params.set('currency', formatQueryParam(data.currency));
  params.set('account_name', formatQueryParam(data.account_name ?? data.agencyName));
  params.set('account_id', formatQueryParam(data.account_id ?? data.agencyId));
  params.set('text', data.text ?? '');

  const sortingField = data.sorting_field ?? data.sortingField;
  if (sortingField) params.set('sorting_field', sortingField);

  const sorting = data.sorting;
  if (sorting === 'ascend') params.set('sorting', 'asc');
  else if (sorting === 'descend') params.set('sorting', 'desc');
  else if (sorting === 'asc' || sorting === 'desc') params.set('sorting', sorting);

  params.set('payment_gateway', formatQueryParam(data.payment_gateway ?? data.paymentGateway));
  params.set('payment_type', formatQueryParam(data.payment_method_type ?? data.paymentMethodTypes));
  params.set('user_type', formatQueryParam(data.user_type ?? data.userType));
  params.set('cogs', formatQueryParam(data.cogs));
  params.set('non_login', formatQueryParam(data.non_login ?? data.isNonLogin));
  params.set('cogs_paid_date', data.cogs_paid_date ?? data.cogsPaidDate ?? '');
  params.set('agency_commission_paid', formatQueryParam(data.agency_commission_paid ?? data.agencyCommissionPaid));
  params.set('agency_commission_paid_date', data.agency_commission_paid_date ?? data.agencyCommissionPaidDate ?? '');

  return params.toString();
};

export const getActivites = createAsyncThunk('GET ACTIVITIES', async (data, thunkAPI) => {
  const query = buildActivityBookingsQuery(data);
  const response = await AxiosAdmin.get(`${URL.ACTIVITY_V2}?${query}`, thunkAPI);
  return response.data;
});

export const getActivityDetails = createAsyncThunk('GET ACTIVITY DETAILS', async (data, thunkAPI) => {
  const response = await AxiosAdmin.get(
    `${URL.ACTIVITY_V2}?id=${data.referenceNumber}&page_size=100&limit=100&page=1`,
    thunkAPI
  );
  return response.data;
});

export const resendEmailConfirmation = createAsyncThunk(
  'RESEND_ACTIVITY_EMAIL_CONFIRMATION',
  async ({ requestData, v2Version }, thunkAPI) => {
    if (v2Version) {
      const response = await AxiosAdmin.post(URL.ACTIVITY_NOTIFY_EMAIL_V2, requestData, thunkAPI);
      return response.data;
    } else {
      const response = await axios.post(URL.RESEND_ACTIVITY_CONFIRMATION_EMAIL, requestData, thunkAPI);
      return response.data;
    }
  }
);

export const updateActivityRemark = createAsyncThunk('UPDATE_ACTIVITY_REMARKS', async (data, thunkAPI) => {
  let requestData = {
    remark_id: data?.remark_id || '',
    remark: data?.remark,
  };
  const response = await AxiosAdmin.put(`${URL.ACTIVITY_UPDATE_V2}?id=${data.referenceNumber}`, requestData, thunkAPI);
  return response.data;
});

export const updateActivityBooking = createAsyncThunk('UPDATE_ACTIVITY_BOOKING', async (data, thunkAPI) => {
  const response = await AxiosAdmin.put(`${URL.ACTIVITY_UPDATE_V2}?id=${data.referenceNumber}`, data, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  activityData: null,
  activityDetails: null,
};

export const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {},
  extraReducers: {
    [getActivites.pending]: (state) => {
      state.isLoading = true;
    },
    [getActivites.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.activityData = action.payload.data;
    },
    [getActivites.rejected]: (state, action) => {
      state.isLoading = false;
      state.activityData = null;
      toast.error(action?.payload?.message);
    },

    [updateActivityRemark.pending]: (state) => {
      state.isLoading = true;
    },
    [updateActivityRemark.fulfilled]: (state) => {
      state.isLoading = false;
      toast.success('Remarks updated successfully');
    },
    [updateActivityRemark.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating remarks');
    },

    [getActivityDetails.pending]: (state) => {
      state.isLoading = true;
      state.activityDetails = null;
    },
    [getActivityDetails.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.activityDetails = action.payload.data;
    },
    [getActivityDetails.rejected]: (state, action) => {
      state.isLoading = false;
      state.activityDetails = null;
      toast.error(action?.payload?.message);
    },

    [resendEmailConfirmation.pending]: (state) => {
      state.isLoading = true;
    },
    [resendEmailConfirmation.fulfilled]: (state) => {
      state.isLoading = false;
      toast.success('Email confirmation resent successfully');
    },
    [resendEmailConfirmation.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error resending email confirmation');
    },

    [updateActivityBooking.pending]: (state) => {
      state.isLoading = true;
    },
    [updateActivityBooking.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload?.message);
    },
    [updateActivityBooking.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating booking data');
    },
  },
});

export default activitySlice.reducer;
